import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Button,
  Label,
  Input,
  Form,
  Alert,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import { createUserApi, updateUserApi, getUserApi } from "helpers/auth_api"

interface BreadcrumbItem {
  title: string
  link: string
}

interface AddUserProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const ROLE_OPTIONS = [
  { value: "Super-admin", label: "Super-admin" },
  { value: "Admin", label: "Admin" },
  { value: "Buyer", label: "Buyer" },
  { value: "Seller", label: "Seller" },
  { value: "Broker", label: "Broker" },
]

const AddUser = (props: AddUserProps) => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEditMode = !!id
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobileNumber: "",
    role: "Admin",
  })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadingUser, setLoadingUser] = useState(false)

  useEffect(() => {
    props.setBreadcrumbItems(isEditMode ? "Edit User" : "Add User", [
      { title: "Users", link: "/users" },
      { title: isEditMode ? "Edit User" : "Add User", link: "#" },
    ])
  }, [props, isEditMode])

  useEffect(() => {
    if (!id) return
    setLoadingUser(true)
    getUserApi(id)
      .then((res) => {
        if (res.success && res.data) {
          const d = res.data as Record<string, unknown>
          setForm({
            fullName: String(d.fullName ?? d.username ?? ""),
            email: String(d.email ?? ""),
            password: "",
            confirmPassword: "",
            mobileNumber: String(d.mobileNumber ?? ""),
            role: String(d.role ?? "Admin"),
          })
        }
      })
      .catch(() => setError("Failed to load user"))
      .finally(() => setLoadingUser(false))
  }, [id])


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trim = (s: string) => (s || "").trim()
    if (!trim(form.fullName)) {
      setError("Full name is required")
      return
    }
    if (!trim(form.email)) {
      setError("Email is required")
      return
    }
    if (!isEditMode) {
      if (!trim(form.password)) {
        setError("Password is required")
        return
      }
      if (form.password.length < 6) {
        setError("Password must be at least 6 characters")
        return
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match")
        return
      }
    } else if (form.password || form.confirmPassword) {
      if (form.password.length < 6) {
        setError("Password must be at least 6 characters")
        return
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match")
        return
      }
    }
    setSaving(true)
    if (isEditMode && id) {
      updateUserApi({
        id,
        fullName: trim(form.fullName),
        email: trim(form.email).toLowerCase(),
        role: form.role,
        mobileNumber: trim(form.mobileNumber) || undefined,
        ...(form.password ? { password: form.password } : {}),
      })
        .then(() => navigate("/users"))
        .catch((err: any) => setError(err?.response?.data?.message || err?.message || "Failed to update user"))
        .finally(() => setSaving(false))
    } else {
      createUserApi({
        fullName: trim(form.fullName),
        email: trim(form.email).toLowerCase(),
        password: form.password,
        role: form.role,
        mobileNumber: trim(form.mobileNumber) || undefined,
      })
        .then(() => navigate("/users"))
        .catch((err: any) => setError(err?.response?.data?.message || err?.message || "Failed to create user"))
        .finally(() => setSaving(false))
    }
  }


  if (loadingUser) {
    return (
      <Row>
        <Col xs="12">
          <div className="text-center py-5 text-muted">Loading user...</div>
        </Col>
      </Row>
    )
  }

  return (
    <Row>
      <Col xs="12">
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <CardTitle tag="h5" className="mb-0">
              {isEditMode ? "Edit User" : "Add User"}
            </CardTitle>
            <Button color="secondary" outline size="sm" onClick={() => navigate("/users")}>
              <i className="mdi mdi-arrow-left me-1" />
              Back to User List
            </Button>
          </CardHeader>
          <CardBody>
            <p className="text-muted small mb-4">
              {isEditMode
                ? "Update user details. Leave password blank to keep current password."
                : "Create a new user. Select the role from the dropdown (Super-admin, Admin, Buyer, Seller, Broker)."}
            </p>
            <Form onSubmit={handleSubmit}>
              {error && <Alert color="danger" className="mb-3">{error}</Alert>}
              <Row className="g-3">
                <Col md={6}>
                  <Label for="fullName" className="fw-semibold">Full Name *</Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Full name"
                    value={form.fullName}
                    onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Label for="email" className="fw-semibold">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Label for="password" className="fw-semibold">{isEditMode ? "New Password (optional)" : "Password *"}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={isEditMode ? "Leave blank to keep current" : "Min 6 characters"}
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    required={!isEditMode}
                  />
                </Col>
                <Col md={6}>
                  <Label for="confirmPassword" className="fw-semibold">{isEditMode ? "Confirm New Password" : "Confirm Password *"}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder={isEditMode ? "Leave blank to keep current" : "Re-enter password"}
                    value={form.confirmPassword}
                    onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                    required={!isEditMode}
                  />
                </Col>
                <Col md={6}>
                  <Label for="mobileNumber" className="fw-semibold">Mobile (optional)</Label>
                  <Input
                    id="mobileNumber"
                    type="tel"
                    placeholder="Mobile number"
                    value={form.mobileNumber}
                    onChange={(e) => setForm((p) => ({ ...p, mobileNumber: e.target.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Label for="role" className="fw-semibold">Role *</Label>
                  <Input
                    id="role"
                    type="select"
                    value={form.role}
                    onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
                    required
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Input>
                </Col>
                <Col xs={12} className="pt-2">
                  <div className="d-flex gap-2">
                    <Button type="submit" color="primary" disabled={saving}>
                      {saving ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update User" : "Create User")}
                    </Button>
                    <Button type="button" color="secondary" outline onClick={() => navigate("/users")}>
                      Cancel
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

const mapStateToProps = () => ({})
const mapDispatchToProps = (dispatch: any) => ({
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) =>
    dispatch(setBreadcrumbItems(title, items)),
})

export default connect(mapStateToProps, mapDispatchToProps)(AddUser)
